import { filter, forEach, isEmpty, map, pick } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';

import { createIndividualCasesForSlides } from '../ProceduresPage/usePendingSlides';
import { assignManifestDraftCaseIds, ManifestDialogUrlState } from './dialogState';
import { readManifestRows } from './helpers';
import {
  collectManifestValidationErrors,
  resolveUploadedManifestRows,
  UploadedManifestResolution,
} from './manifestResolution';

interface ManifestFileResolution {
  resolution: UploadedManifestResolution;
  errorMessages: string[];
}

export const resolveManifestFileRows = async ({
  file,
  editableExistingAndMockCases,
  pendingCaseCount,
  procedureFieldContext,
  labId,
  studyId,
  fetchSlidesByIdBatches,
  isCurrentUpload,
  excludedRowNumbers,
}: {
  file: File;
  editableExistingAndMockCases: Procedure[];
  existingProcedures: Procedure[];
  pendingCaseCount: number;
  procedureFieldContext: ProceduresFieldsContext;
  labId: string;
  studyId: string;
  fields: Parameters<typeof collectManifestValidationErrors>[0]['fields'];
  rowsPerPage: number;
  fetchSlidesByIdBatches: (slideIds: string[]) => Promise<Slide[]>;
  isCurrentUpload: () => boolean;
  excludedRowNumbers?: number[];
}): Promise<ManifestFileResolution | null> => {
  const excelRows = await readManifestRows(file);
  if (!isCurrentUpload()) return null;
  let resolutionInput = {
    editableExistingAndMockCases,
    notEditableSlidesMockCases: [] as Procedure[],
    excelRows,
    procedureFieldContext,
    labId,
    studyId,
    excludedRowNumbers,
    preserveInvalidRows: true,
    slideLookupComplete: false,
  };
  let resolution = resolveUploadedManifestRows(resolutionInput);
  if (!isEmpty(resolution.slideIdsToFetch)) {
    const slideIdsToFetch = resolution.slideIdsToFetch;
    const fetchedSlides = await fetchSlidesByIdBatches(slideIdsToFetch);
    if (!isCurrentUpload()) return null;
    resolutionInput = {
      ...resolutionInput,
      slideLookupComplete: true,
      notEditableSlidesMockCases: map(createIndividualCasesForSlides(fetchedSlides, labId), (mockCase, index) => ({
        ...mockCase,
        id: pendingCaseCount + index,
      })),
    };
    resolution = resolveUploadedManifestRows(resolutionInput);
  }
  return { resolution, errorMessages: resolution.errorMessages };
};

export const replaceManifestDraftFromUpload = ({
  previousState,
  resolution,
  existingProcedures,
}: {
  previousState: ManifestDialogUrlState;
  resolution: UploadedManifestResolution;
  existingProcedures: Procedure[];
}): ManifestDialogUrlState => {
  const uploadOutcomeWarnings = isEmpty(resolution.manifestCases)
    ? [
        ...resolution.warningMessages,
        isEmpty(resolution.notIngestedSlides)
          ? 'No manifest rows were generated from this file. Check the slide identifiers and upload again.'
          : 'No manifest rows were generated from this file. Review the Not Ingested Slides tab or update the slide identifiers and upload again.',
      ]
    : resolution.warningMessages;
  const uploadedTargets: NonNullable<ManifestDialogUrlState['existingCaseAssignmentsBySlideId']> = {};
  const resolvedCases = resolution.manifestCases.map((procedure) => {
    const matchingCases = filter(existingProcedures, { label: procedure.label });
    const target = procedure.draftIssues?.some((issue) => issue.field === 'targetCaseAssignment')
      ? null
      : procedure.draftTarget === 'new'
      ? 'new'
      : matchingCases.length === 1
      ? matchingCases[0].id
      : matchingCases.length
      ? null
      : 'new';
    forEach(procedure.slides, (slide) => {
      uploadedTargets[slide.id] = target;
    });
    return { ...procedure, draftTarget: target };
  });
  const draftCases = assignManifestDraftCaseIds({
    cases: resolvedCases,
    retainedCases: previousState.draftCases || [],
    casePatchesById: previousState.casePatchesById,
  });
  return {
    ...previousState,
    existingCaseAssignmentsBySlideId: uploadedTargets,
    draftCases,
    draftWarnings: uploadOutcomeWarnings,
    operationErrors: [],
    manualManifestSlideIdsToLoad: [],
    manualManifestGroupIntoSingleCase: false,
    manualManifestErrorMessages: [],
    casePatchesById: Object.fromEntries(
      draftCases.map((procedure) => [procedure.id, pick(procedure, ['label', 'cancerTypeId', 'cancerSubtypes'])])
    ),
    slidePatchesById: {},
    removedSlideIds: [],
    showOnlyErrors: false,
    showOnlyUnassignedSlides: false,
    page: 1,
  };
};
